import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';

import API from 'api';
import { QUERY_KEYS } from 'api/certificates/queries';
import { DocumentDetailsModel, DOCUMENT_STATUS } from 'api/documents/documents.model';
import { ErrorContent } from 'components/FetchingErrorBoundary';
import Loading from 'components/Icons/Loading';
import { CertificateDetailsModel } from 'models/certificate.model';
import { CATEGORY_TYPE } from 'models/documents.model';

import BatteryCells from './BatteryCells';
import BatteryPackRepair from './BatteryPackRepair';
import BatteryPacks from './BatteryPacks';
import BatteryPacksRecycling from './BatteryPacksRecycling';
import BatteryRepurpose from './BatteryRepurpose';
import BatteryTechReview from './BatteryTechReview';
import BatteryWaste from './BatteryWaste';
import Chemistry from './Chemistry';
import Other from './Other';

const CertificateDetailsPage = () => {
  const { id } = useParams();

  const {
    data: document,
    isLoading,
    refetch,
  } = useQuery<DocumentDetailsModel<CertificateDetailsModel>>(
    [QUERY_KEYS.GET_CERTIFICATES, id],
    () => API.documents.getDocumentsID<CertificateDetailsModel>(id ?? ''),
    {
      useErrorBoundary: true,
      refetchInterval: (response?: DocumentDetailsModel<CertificateDetailsModel>) => {
        if (
          response &&
          [DOCUMENT_STATUS.PREPARED_TO_SIGN, DOCUMENT_STATUS.UPLOADING].includes(
            response.history[0].status
          )
        ) {
          return 2000;
        }

        return false;
      },
    }
  );

  if (isLoading) return <Loading />;

  if (!document) return <ErrorContent onRetry={refetch} />;

  switch (document.category) {
    case CATEGORY_TYPE.CHEMISTRY:
      return <Chemistry {...document} />;

    case CATEGORY_TYPE.BATTERY_CELLS:
      return <BatteryCells {...document} />;

    case CATEGORY_TYPE.BATTERY_PACKS:
      return <BatteryPacks {...document} />;

    case CATEGORY_TYPE.BATTERY_TECH_REVIEW:
      return <BatteryTechReview {...document} />;

    case CATEGORY_TYPE.BATTERY_PACKS_REPAIR:
      return <BatteryPackRepair {...document} />;

    case CATEGORY_TYPE.BATTERY_REPURPOSE:
      return <BatteryRepurpose {...document} />;

    case CATEGORY_TYPE.BATTERY_PACKS_RECYCLING:
      return <BatteryPacksRecycling {...document} />;

    case CATEGORY_TYPE.BATTERY_WASTE:
      return <BatteryWaste {...document} />;

    case CATEGORY_TYPE.CERTIFICATE:
    default:
      return <Other {...document} />;
  }
};

export default CertificateDetailsPage;
